import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import useWs from '../../../hooks/useWs';
import SAR from '../../../components/SAR';
import { useDialog } from '../../../context/DialogContext';

const I = {
  search:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  plus:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  eye:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  x:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  user:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  map:     <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  truck:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  refresh: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  check:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  ship:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2s2.5 2 5 2 2.5-2 5-2c1.3 0 1.9.5 2.5 1"/><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.2.5 4.1 1.62 6"/><path d="M3 11V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4"/><path d="M12 7v4"/></svg>,
  star:    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
  alert:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  water:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  cancel:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
};

const STATUS = {
  pending:           { label: 'قيد الانتظار',          color: 'warning', next: ['confirmed','cancelled'] },
  confirmed:         { label: 'مؤكد',                  color: 'info',    next: ['assigned','cancelled'] },
  assigned:          { label: 'سائق معيّن',             color: 'info',    next: ['accepted','cancelled'] },
  accepted:          { label: 'مقبول – قيد التنفيذ',   color: 'primary', next: ['en_route_pickup','cancelled'], highlight: true },
  en_route_pickup:   { label: 'في الطريق للتعبئة',      color: 'blue',    next: ['at_source','cancelled'] },
  at_source:         { label: 'وصل محطة التعبئة',       color: 'blue',    next: ['loading','cancelled'] },
  loading:           { label: 'جارٍ التعبئة',           color: 'blue',    next: ['en_route_delivery','cancelled'] },
  en_route_delivery: { label: 'في الطريق للعميل',       color: 'blue',    next: ['arrived','cancelled'] },
  arrived:           { label: 'وصل للعميل',             color: 'blue',    next: ['delivering','cancelled'] },
  delivering:        { label: 'جارٍ التسليم',           color: 'blue',    next: ['completed','cancelled'] },
  completed:         { label: 'تم التوصيل',             color: 'success', next: [] },
  cancelled:         { label: 'ملغي',                   color: 'danger',  next: [] },
  failed:            { label: 'فشل',                   color: 'danger',  next: [] },
  on_hold:           { label: 'معلّق',                  color: 'warning', next: ['confirmed','cancelled'] },
  no_driver_available:{ label: 'لا سائق متاح',           color: 'danger',  next: ['cancelled'], highlight: false },
};

const WATER_LABELS = { drinking: 'مياه شرب 💧', non_drinking: 'مياه غير صالحة 🏭', agricultural: 'مياه زراعية 🌱' };
const INIT_FORM = { customer_id:'', customer_name:'', customer_phone:'', water_type:'drinking', quantity_tons:'', delivery_address:'', ship_id:'', pricing_config_id:'', notes:'', payment_method:'cash', scheduled_for:'' };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA',{year:'numeric',month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '-';
const Money = ({ n }) => n != null ? <>{parseFloat(n).toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></> : <>-</>;
const Stars = ({n}) => n ? <span style={{color:'#f59e0b',fontSize:'12px'}}>{'★'.repeat(Math.round(n))}{'☆'.repeat(5-Math.round(n))} {parseFloat(n).toFixed(1)}</span> : null;

const OrdersPage = () => {
  const { apiCall, tenant, getToken } = useTenantAuth();
  const { success, error: showError } = useToast();
  const { confirm } = useDialog();
  const searchTimer = useRef(null);
  const knownPendingIds = useRef(null);
  const [newOrderAlert, setNewOrderAlert] = useState(null);

  /* --- list state --- */
  const [loading, setLoading]         = useState(true);
  const [orders, setOrders]           = useState([]);
  const [stats, setStats]             = useState({});
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatus]     = useState('all');
  const [dateFilter, setDate]         = useState('all');

  /* --- detail modal --- */
  const [sel, setSel]                 = useState(null);
  const [showDetail, setShowDetail]   = useState(false);
  const [drivers, setDrivers]         = useState([]);
  const [loadDrv, setLoadDrv]         = useState(false);
  const [assigning, setAssigning]     = useState(false);
  const [diagnose, setDiagnose]       = useState(null);
  const [loadDiag, setLoadDiag]       = useState(false);

  /* --- new order modal --- */
  const [showNew, setShowNew]         = useState(false);
  const [step, setStep]               = useState(1);
  const [form, setForm]               = useState(INIT_FORM);
  const [submitting, setSubmitting]   = useState(false);
  const [ships, setShips]             = useState([]);
  const [pricings, setPricings]       = useState([]);
  const [loadShips, setLoadShips]     = useState(false);
  const [custResults, setCustResults] = useState([]);
  const [custLoading, setCustLoading] = useState(false);

  /* ===================== loaders ===================== */
  const fetchOrders = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const p = new URLSearchParams({ skip: 0, limit: 100 });
      if (search)              p.append('search', search);
      if (statusFilter!=='all') p.append('status', statusFilter);
      if (dateFilter==='today') { const t=new Date(); p.append('date_from', t.toISOString().split('T')[0]); p.append('date_to', t.toISOString().split('T')[0]); }
      if (dateFilter==='week')  { const t=new Date(),w=new Date(t); w.setDate(t.getDate()-7); p.append('date_from',w.toISOString().split('T')[0]); }
      if (dateFilter==='month') { const t=new Date(); p.append('date_from',`${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-01`); }
      const [ord, st] = await Promise.all([apiCall(`/tenant/orders?${p}`), apiCall('/tenant/orders/stats')]);
      const items = ord.items || [];
      setOrders(items);
      setStats(st || {});
      if (silent && knownPendingIds.current !== null) {
        const pendingNow = items.filter(o => o.status === 'pending').map(o => o.id);
        const newOnes = pendingNow.filter(id => !knownPendingIds.current.has(id));
        if (newOnes.length > 0) {
          setNewOrderAlert(`${newOnes.length === 1 ? 'طلب جديد' : `${newOnes.length} طلبات جديدة`} بانتظار التأكيد`);
          setTimeout(() => setNewOrderAlert(null), 8000);
        }
      }
      knownPendingIds.current = new Set(items.filter(o => o.status === 'pending').map(o => o.id));
    } catch { if (!silent) showError('فشل تحميل الطلبات'); }
    finally { if (!silent) setLoading(false); }
  }, [apiCall, showError, search, statusFilter, dateFilter]);

  const loadOrders = useCallback(() => fetchOrders(false), [fetchOrders]);

  useEffect(() => {
    fetchOrders(false);
    const t = setInterval(() => fetchOrders(true), 30000); // fallback only
    return () => clearInterval(t);
  }, [fetchOrders]);

  // WS: instant update when any order event fires
  const wsToken = getToken ? getToken() : localStorage.getItem('watrly_tenant_token') || '';
  useWs(
    tenant?.id ? `/tenant/${tenant.id}` : null,
    useCallback((data) => {
      if (data.type === 'order_status' && data.order_id && data.status) {
        // ── 1. Instant in-place update (no API round-trip) ──────────────
        setOrders(prev =>
          prev.map(o => o.id === data.order_id ? { ...o, status: data.status } : o)
        );
        // ── 2. Sync open detail modal if it's for the same order ────────
        setSel(prev => {
          if (!prev || prev.id !== data.order_id) return prev;
          const patch = { status: data.status };
          if (data.driver_id) patch.current_driver_id = data.driver_id;
          return { ...prev, ...patch };
        });
      }
      // Full background refresh (for stats, new orders, driver name, etc.)
      fetchOrders(true);
      if (data.type === 'dispatch_expired') {
        setNewOrderAlert(`⚠️ ${data.message || 'انتهت موجات التعيين — يرجى التعيين اليدوي'}`);
        setTimeout(() => setNewOrderAlert(null), 12000);
      } else if (data.type === 'order_cancelled') {
        setNewOrderAlert(`❌ طلب أُلغي تلقائياً: ${data.reason || ''}`);
        setTimeout(() => setNewOrderAlert(null), 8000);
      }
    }, [fetchOrders]),
    wsToken
  );

  /* debounce search */
  const onSearch = (v) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {}, 0);
  };

  /* diagnose dispatch */
  const runDiagnose = async (orderId) => {
    setLoadDiag(true); setDiagnose(null);
    try { const r = await apiCall(`/tenant/dispatch/diagnose/${orderId}`); setDiagnose(r); }
    catch(e) { showError(e.message || 'فشل التشخيص'); }
    finally { setLoadDiag(false); }
  };

  /* open detail */
  const openDetail = async (o) => {
    setSel(o); setShowDetail(true); setDrivers([]); setDiagnose(null);
    if (['pending','confirmed','assigned'].includes(o.status)) {
      setLoadDrv(true);
      try { const r = await apiCall(`/tenant/orders/${o.id}/nearby-drivers`); setDrivers(r.drivers || []); }
      catch { }
      finally { setLoadDrv(false); }
    }
  };

  /* status update */
  const updateStatus = async (orderId, newStatus) => {
    try {
      await apiCall(`/tenant/orders/${orderId}/status`, { method:'PUT', body:JSON.stringify({status:newStatus}) });
      success('تم تحديث الحالة');
      setSel(p => p ? {...p, status:newStatus} : p);
      loadOrders();
    } catch(e) { showError(e.message||'فشل تحديث الحالة'); }
  };

  /* assign driver */
  const assignDriver = async (driverId) => {
    if (!sel) return;
    setAssigning(true);
    try {
      await apiCall(`/tenant/orders/${sel.id}/assign`, { method:'PUT', body:JSON.stringify({driver_id:driverId}) });
      success('تم تعيين السائق بنجاح');
      setSel(p => p ? {...p, status:'assigned'} : p);
      loadOrders();
    } catch(e) { showError(e.message||'فشل تعيين السائق'); }
    finally { setAssigning(false); }
  };

  /* cancel */
  const cancelOrder = async (orderId) => {
    if (!await confirm({ title: 'إلغاء الطلب', message: 'تأكيد إلغاء الطلب؟', type: 'warning', confirmText: 'إلغاء', cancelText: 'تراجع' })) return;
    try {
      await apiCall(`/tenant/orders/${orderId}/cancel`, { method:'PUT', body:JSON.stringify({}) });
      success('تم إلغاء الطلب');
      setShowDetail(false); loadOrders();
    } catch(e) { showError(e.message||'فشل الإلغاء'); }
  };

  /* customer search */
  const searchCust = async (q) => {
    setForm(p=>({...p,customer_id:'',customer_name:'',customer_phone:''}));
    if (!q || q.length < 2) { setCustResults([]); return; }
    setCustLoading(true);
    try { const r = await apiCall(`/tenant/orders/customers/search?q=${encodeURIComponent(q)}`); setCustResults(r||[]); }
    catch { }
    finally { setCustLoading(false); }
  };

  /* load ships for step 2 */
  const fetchShips = async () => {
    setLoadShips(true);
    try {
      const r = await apiCall(`/tenant/orders/suitable-ships?water_type=${form.water_type}${form.customer_id?`&customer_id=${form.customer_id}`:''}`);
      setShips(r.ships||[]); setPricings(r.pricing_configs||[]);
      if (r.pricing_configs?.length===1) setForm(p=>({...p,pricing_config_id:r.pricing_configs[0].id}));
    } catch { showError('فشل تحميل الشيبات'); }
    finally { setLoadShips(false); }
  };

  /* pricing preview calc */
  const calcPreview = () => {
    const cfg = pricings.find(p=>p.id===form.pricing_config_id);
    if (!cfg || !form.quantity_tons) return null;
    const qty = parseFloat(form.quantity_tons)||0;
    const sub = qty * cfg.base_price_per_ton;
    const tax = sub * (cfg.tax_rate/100);
    const del = cfg.delivery_included ? 0 : parseFloat(cfg.delivery_flat_fee||0);
    return { sub, tax, del, total: sub+tax+del, cfg };
  };

  /* submit new order */
  const submitOrder = async () => {
    if (!form.customer_id) { showError('اختر عميلاً'); return; }
    if (!form.quantity_tons||parseFloat(form.quantity_tons)<=0) { showError('أدخل كمية صحيحة'); return; }
    setSubmitting(true);
    try {
      const r = await apiCall('/tenant/orders', { method:'POST', body:JSON.stringify({
        customer_id: form.customer_id,
        water_type: form.water_type,
        quantity_tons: parseFloat(form.quantity_tons),
        delivery_address: form.delivery_address||null,
        ship_id: form.ship_id||null,
        pricing_config_id: form.pricing_config_id||null,
        notes: form.notes||null,
        payment_method: form.payment_method||null,
        scheduled_for: form.scheduled_for||null,
      })});
      success(`تم إنشاء الطلب ${r.order_number}`);
      setShowNew(false); setForm(INIT_FORM); setStep(1); loadOrders();
    } catch(e) { showError(e.message||'فشل إنشاء الطلب'); }
    finally { setSubmitting(false); }
  };

  const acceptedOrders = orders.filter(o=>o.status==='accepted'||o.status==='delivering'||o.status==='en_route_delivery');
  const preview = calcPreview();

  return (
    <div className="op-page">
      {/* New order floating alert */}
      {newOrderAlert && (
        <div
          onClick={() => { setStatus('pending'); setNewOrderAlert(null); }}
          style={{
            position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
            zIndex: 9999, background: '#f59e0b', color: '#1e1b4b',
            fontWeight: 700, fontSize: 15, borderRadius: 12,
            padding: '14px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
            animation: 'pulse 1s infinite',
          }}
        >
          🔔 {newOrderAlert} — اضغط للعرض
        </div>
      )}

      {/* Header */}
      <div className="op-header">
        <div><h1>إدارة الطلبات</h1><p>{orders.length} طلب إجمالي</p></div>
        <div className="op-header__actions">
          <button className="op-btn op-btn--ghost" onClick={loadOrders}>{I.refresh} تحديث</button>
          <button className="op-btn op-btn--primary" onClick={()=>{setShowNew(true);setStep(1);setForm(INIT_FORM);setCustResults([]);}}>{I.plus} طلب جديد</button>
        </div>
      </div>

      {/* Stats */}
      <div className="op-stats">
        {Object.entries(STATUS).map(([k,v])=>(
          <div key={k} className={`op-stat ${statusFilter===k?'op-stat--active':''} ${v.highlight?'op-stat--hl':''}`} onClick={()=>setStatus(s=>s===k?'all':k)}>
            <span className="op-stat__num">{stats[k]||0}</span>
            <span className="op-stat__lbl">{v.label}</span>
          </div>
        ))}
      </div>

      {/* Accepted banner */}
      {acceptedOrders.length > 0 && (
        <div className="op-accepted-banner">
          <span>{I.truck}</span>
          <strong>{acceptedOrders.length} طلب مقبول من سواقين وقيد التنفيذ الآن</strong>
          <div className="op-accepted-pills">
            {acceptedOrders.map(o=>(
              <button key={o.id} className="op-accepted-pill" onClick={()=>openDetail(o)}>
                {o.order_number} — {o.customer?.name||'-'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="op-filters">
        <div className="op-search">
          {I.search}
          <input value={search} onChange={e=>onSearch(e.target.value)} placeholder="بحث برقم الطلب أو اسم العميل..." />
        </div>
        <select value={statusFilter} onChange={e=>setStatus(e.target.value)} className="op-select">
          <option value="all">جميع الحالات</option>
          {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={dateFilter} onChange={e=>setDate(e.target.value)} className="op-select">
          <option value="all">جميع الفترات</option>
          <option value="today">اليوم</option>
          <option value="week">هذا الأسبوع</option>
          <option value="month">هذا الشهر</option>
        </select>
      </div>

      {/* Table */}
      {loading ? <div className="op-loading"><div className="op-spinner"/></div> : orders.length===0 ? (
        <div className="op-empty">{I.water}<h3>لا توجد طلبات</h3><p>أنشئ طلبًا جديدًا أو عدّل الفلاتر</p></div>
      ) : (
        <div className="op-table-wrap">
          <table className="op-table">
            <thead><tr>
              <th>رقم الطلب</th><th>العميل</th><th>نوع المياه</th>
              <th>الكمية</th><th>المبلغ</th><th>الشيب</th><th>السائق</th>
              <th>الحالة</th><th>التاريخ</th><th></th>
            </tr></thead>
            <tbody>
              {orders.map(o => {
                const st = STATUS[o.status] || {label:o.status, color:'info'};
                return (
                  <tr key={o.id} className={`op-tr ${st.highlight?'op-tr--accepted':''}`}>
                    <td><span className="op-order-num">{o.order_number}</span></td>
                    <td><div className="op-cell-user"><span>{o.customer?.name ? o.customer.name : '-'}</span><small>{o.customer?.phone ? o.customer.phone : ''}</small></div></td>
                    <td><span className="op-water-tag">{WATER_LABELS[o.water_type] ? WATER_LABELS[o.water_type] : o.water_type ? o.water_type : '-'}</span></td>
                    <td><strong>{o.quantity_tons ? o.quantity_tons : o.requested_quantity ? o.requested_quantity : '-'} طن</strong></td>
                    <td><strong className="op-amount"><Money n={o.total_amount} /></strong></td>
                    <td>{o.ship?.name ? o.ship.name : <span style={{color:'var(--tenant-text-secondary,#aaa)'}}>—</span>}</td>
                    <td>{o.driver?.name ? o.driver.name : <span className="op-no-driver">{I.alert} لا يوجد</span>}</td>
                    <td><span className={`op-badge op-badge--${st.color}`}>{st.label}</span></td>
                    <td><span className="op-date">{fmtDate(o.created_at)}</span></td>
                    <td><button className="op-eye-btn" onClick={()=>openDetail(o)}>{I.eye}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ======= ORDER DETAIL MODAL ======= */}
      {showDetail && sel && (
        <div className="op-overlay" onClick={()=>setShowDetail(false)}>
          <div className="op-modal op-modal--lg" onClick={e=>e.stopPropagation()}>
            <div className="op-modal__head">
              <div>
                <h2>طلب #{sel.order_number}</h2>
                <span className={`op-badge op-badge--${STATUS[sel.status]?.color||'info'}`}>{STATUS[sel.status]?.label||sel.status}</span>
              </div>
              <button onClick={()=>setShowDetail(false)}>{I.x}</button>
            </div>
            <div className="op-modal__body">
              {/* Info grid */}
              <div className="op-detail-grid">
                <div className="op-detail-card">
                  <div className="op-detail-card__title">{I.user} معلومات العميل</div>
                  <div className="op-detail-rows">
                    <div><span>الاسم</span><strong>{sel.customer?.name||sel.customer_name||'-'}</strong></div>
                    <div><span>الجوال</span><strong dir="ltr">{sel.customer?.phone||sel.customer_phone||'-'}</strong></div>
                    <div><span>العنوان</span><strong>{sel.delivery_address||'-'}</strong></div>
                  </div>
                </div>
                <div className="op-detail-card">
                  <div className="op-detail-card__title">{I.water} تفاصيل الطلب</div>
                  <div className="op-detail-rows">
                    <div><span>نوع المياه</span><strong>{WATER_LABELS[sel.water_type]||sel.water_type||'-'}</strong></div>
                    <div><span>الكمية</span><strong>{sel.quantity_tons||sel.requested_quantity||'-'} طن</strong></div>
                    <div><span>الإجمالي</span><strong style={{color:'var(--tenant-primary)'}}><Money n={sel.discount_amount > 0 ? sel.total_amount - sel.discount_amount : sel.total_amount} /></strong></div>
                    {sel.ship?.name && <div><span>الشيب</span><strong>{sel.ship.name}</strong></div>}
                    {sel.delivery_fee!=null && <div><span>رسوم التوصيل</span><strong><Money n={sel.delivery_fee} /></strong></div>}
                    {sel.tax_amount!=null && <div><span>الضريبة</span><strong><Money n={sel.tax_amount} /></strong></div>}
                    {sel.discount_amount > 0 && <div><span>خصم كوبون {sel.coupon_code && `(${sel.coupon_code})`}</span><strong style={{color:'#16a34a'}}>- <Money n={sel.discount_amount} /></strong></div>}
                  </div>
                </div>
              </div>

              {/* Dispatch Diagnose */}
              {['pending','confirmed','no_driver_available'].includes(sel.status) && (
                <div className="op-assign-section" style={{marginBottom:'12px'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
                    <div className="op-section-title" style={{margin:0}}>{I.alert} تشخيص الـ Dispatch</div>
                    <button className="op-btn op-btn--ghost op-btn--sm" disabled={loadDiag} onClick={()=>runDiagnose(sel.id)}>
                      {loadDiag ? '...' : '🔍 تشخيص'}
                    </button>
                  </div>
                  {diagnose && (
                    <div style={{fontSize:'12px',border:'1px solid #e5e7eb',borderRadius:'8px',overflow:'hidden'}}>
                      <div style={{background:'#f9fafb',padding:'8px 12px',borderBottom:'1px solid #e5e7eb',display:'flex',gap:'16px',flexWrap:'wrap'}}>
                        <span>🚢 <strong>{diagnose.ship_name||'—'}</strong></span>
                        <span>📦 {diagnose.requested_qty_tons} طن</span>
                        <span>📡 نطاق البحث: {diagnose.search_radius_km} كم</span>
                        <span>✅ مؤهل: <strong style={{color:diagnose.eligible_count>0?'#16a34a':'#dc2626'}}>{diagnose.eligible_count} سائق</strong></span>
                      </div>
                      {diagnose.drivers.map(d=>(
                        <div key={d.driver_id} style={{padding:'8px 12px',borderBottom:'1px solid #f3f4f6',background:d.eligible?'#f0fdf4':'#fff'}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                            <strong style={{color:d.eligible?'#16a34a':'#374151'}}>
                              {d.eligible?'✅':'❌'} {d.full_name}
                            </strong>
                            <span style={{color:'#6b7280'}}>{d.distance_km!=null?`${d.distance_km} كم`:''}</span>
                          </div>
                          {d.issues.length>0 && (
                            <ul style={{margin:'4px 0 0 0',padding:'0 16px',color:'#dc2626'}}>
                              {d.issues.map((iss,i)=><li key={i}>{iss}</li>)}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Driver assignment (when pending/confirmed) */}
              {['pending','confirmed','assigned'].includes(sel.status) && (
                <div className="op-assign-section">
                  <div className="op-section-title">{I.truck} تعيين سائق قريب من الشيب</div>
                  {loadDrv ? (
                    <div className="op-loading-sm"><div className="op-spinner op-spinner--sm"/> جاري البحث عن سواقين متاحين...</div>
                  ) : drivers.length===0 ? (
                    <div className="op-no-drivers">{I.alert} لا يوجد سواقون متاحون حالياً بالقرب من الشيب</div>
                  ) : (
                    <div className="op-drivers-list">
                      {drivers.map(d=>(
                        <div key={d.id} className="op-driver-card">
                          <div className="op-driver-card__info">
                            <div className="op-driver-card__name">{d.full_name}</div>
                            <div className="op-driver-card__meta">
                              <Stars n={d.rating}/>
                              {d.distance_km!=null && (
                                <span className="op-dist" title="مسافة الخط المستقيم (ليست مسافة الطريق)">
                                  {I.map} {d.distance_km} كم ↗
                                </span>
                              )}
                              {d.vehicle && (
                                <span>
                                  {d.vehicle.make} {d.vehicle.model} — {d.vehicle.plate}
                                  {d.vehicle.capacity_tons != null && (
                                    <strong style={{marginRight:'4px',color:'var(--tenant-primary)'}}>
                                      ({d.vehicle.capacity_tons} طن)
                                    </strong>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          <button className="op-btn op-btn--primary op-btn--sm" disabled={assigning} onClick={()=>assignDriver(d.id)}>
                            {assigning?'...':I.check} تعيين
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Status flow */}
              <div className="op-status-section">
                <div className="op-section-title">تحديث حالة الطلب</div>
                <div className="op-status-btns">
                  {(STATUS[sel.status]?.next||[]).map(ns=>(
                    <button key={ns} className={`op-btn op-btn--${STATUS[ns]?.color||'ghost'}`} onClick={()=>updateStatus(sel.id, ns)}>
                      {STATUS[ns]?.label}
                    </button>
                  ))}
                  {!['completed','cancelled','failed'].includes(sel.status) && (
                    <button className="op-btn op-btn--danger-ghost" onClick={()=>cancelOrder(sel.id)}>{I.cancel} إلغاء الطلب</button>
                  )}
                </div>
              </div>

              {/* Notes */}
              {sel.notes && <div className="op-notes"><span>ملاحظات:</span> {sel.notes}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ======= NEW ORDER MODAL ======= */}
      {showNew && (
        <div className="op-overlay" onClick={()=>setShowNew(false)}>
          <div className="op-modal op-modal--new" onClick={e=>e.stopPropagation()}>
            <div className="op-modal__head">
              <div>
                <h2>طلب جديد</h2>
                <div className="op-steps">
                  {['العميل والنوع','الشيب والتسعير','تأكيد'].map((s,i)=>(
                    <span key={i} className={`op-step ${step===i+1?'op-step--active':step>i+1?'op-step--done':''}`}>{i+1}. {s}</span>
                  ))}
                </div>
              </div>
              <button onClick={()=>setShowNew(false)}>{I.x}</button>
            </div>
            <div className="op-modal__body">

              {/* STEP 1: Customer + water type + quantity */}
              {step===1 && (
                <div className="op-form">
                  <div className="op-fgroup op-fgroup--full">
                    <label>العميل *</label>
                    <div className="op-customer-search">
                      <input
                        placeholder="ابحث بالاسم أو الجوال..."
                        defaultValue={form.customer_name}
                        onChange={e=>searchCust(e.target.value)}
                      />
                      {custLoading && <span className="op-spin-sm"/>}
                      {custResults.length>0 && (
                        <div className="op-cust-drop">
                          {custResults.map(c=>(
                            <div key={c.id} className="op-cust-item" onClick={()=>{setForm(p=>({...p,customer_id:c.id,customer_name:c.name,customer_phone:c.phone}));setCustResults([]);}}>
                              <strong>{c.name}</strong><small dir="ltr">{c.phone}</small>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {form.customer_name && <div className="op-selected-cust">{I.check} {form.customer_name} — {form.customer_phone}</div>}
                  </div>

                  <div className="op-fgroup">
                    <label>نوع المياه *</label>
                    <div className="op-water-btns">
                      {[['drinking','مياه شرب 💧'],['non_drinking','مياه غير صالحة 🏭'],['agricultural','مياه زراعية 🌱']].map(([v,l])=>(
                        <button key={v} type="button" className={`op-water-btn ${form.water_type===v?'active':''}`} onClick={()=>setForm(p=>({...p,water_type:v,ship_id:'',pricing_config_id:''}))}>{l}</button>
                      ))}
                    </div>
                  </div>

                  <div className="op-fgroup">
                    <label>الكمية (طن) *</label>
                    <input type="number" min="0.5" step="0.5" placeholder="مثال: 5" value={form.quantity_tons} onChange={e=>setForm(p=>({...p,quantity_tons:e.target.value}))} className="op-input"/>
                  </div>

                  <div className="op-fgroup op-fgroup--full">
                    <label>عنوان التوصيل</label>
                    <input value={form.delivery_address} onChange={e=>setForm(p=>({...p,delivery_address:e.target.value}))} placeholder="العنوان التفصيلي..." className="op-input"/>
                  </div>

                  <div className="op-fgroup">
                    <label>طريقة الدفع</label>
                    <select value={form.payment_method} onChange={e=>setForm(p=>({...p,payment_method:e.target.value}))} className="op-input">
                      <option value="cash">نقدي</option>
                      <option value="card">بطاقة</option>
                      <option value="mada">مدى</option>
                      <option value="bank_transfer">تحويل بنكي</option>
                    </select>
                  </div>

                  <div className="op-fgroup">
                    <label>جدولة (اختياري)</label>
                    <input type="datetime-local" value={form.scheduled_for} onChange={e=>setForm(p=>({...p,scheduled_for:e.target.value}))} className="op-input"/>
                  </div>

                  <div className="op-fgroup op-fgroup--full">
                    <label>ملاحظات</label>
                    <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} placeholder="أي تعليمات خاصة..." rows={2} className="op-input"/>
                  </div>
                </div>
              )}

              {/* STEP 2: Ship + Pricing */}
              {step===2 && (
                <div className="op-form">
                  {loadShips ? (
                    <div className="op-loading-sm"><div className="op-spinner op-spinner--sm"/> جاري البحث عن أقرب الشيبات المناسبة...</div>
                  ) : (
                    <>
                      <div className="op-fgroup op-fgroup--full">
                        <label>{I.ship} اختر الشيب (مرتب من الأقرب للعميل)</label>
                        <div className="op-ships-list">
                          {ships.length===0 ? (
                            <div className="op-no-drivers">{I.alert} لا توجد شيبات تخدم هذا النوع من المياه حالياً</div>
                          ) : ships.map(s=>(
                            <div key={s.id} className={`op-ship-card ${form.ship_id===s.id?'op-ship-card--sel':''}`} onClick={()=>setForm(p=>({...p,ship_id:s.id}))}>
                              <div className="op-ship-card__info">
                                <strong>{s.name}</strong>
                                <small>{s.address||s.city||''}</small>
                              </div>
                              <div className="op-ship-card__meta">
                                {s.distance_km!=null && <span className="op-dist">{I.map} {s.distance_km} كم</span>}
                                {s.current_stock_tons!=null && <span>مخزون: {s.current_stock_tons} طن</span>}
                                <Stars n={s.rating}/>
                              </div>
                              {form.ship_id===s.id && <span className="op-ship-check">{I.check}</span>}
                            </div>
                          ))}
                        </div>
                      </div>

                      {pricings.length>0 && (
                        <div className="op-fgroup op-fgroup--full">
                          <label>إعداد التسعير</label>
                          <div className="op-pricings">
                            {pricings.map(p=>(
                              <div key={p.id} className={`op-pricing-card ${form.pricing_config_id===p.id?'op-pricing-card--sel':''}`} onClick={()=>setForm(f=>({...f,pricing_config_id:p.id}))}>
                                <strong>{p.name}</strong>
                                <span>{p.base_price_per_ton}{' '}<SAR className="h-[0.75em] w-auto" />/طن — ضريبة {p.tax_rate}% — توصيل {p.delivery_included?'شامل':<>{p.delivery_flat_fee}{' '}<SAR className="h-[0.75em] w-auto" /></>}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {preview && (
                        <div className="op-fgroup op-fgroup--full">
                          <div className="op-price-preview">
                            <div className="op-price-preview__title">معاينة السعر — {form.quantity_tons} طن</div>
                            <div className="op-price-rows">
                              <div><span>المجموع الفرعي ({form.quantity_tons} × {preview.cfg.base_price_per_ton})</span><strong>{preview.sub.toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></strong></div>
                              {preview.del>0 && <div><span>التوصيل</span><strong>{preview.del.toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></strong></div>}
                              {preview.tax>0 && <div><span>الضريبة {preview.cfg.tax_rate}%</span><strong>{preview.tax.toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></strong></div>}
                              <div className="op-price-rows__total"><span>الإجمالي</span><strong>{preview.total.toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></strong></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* STEP 3: Confirm */}
              {step===3 && (
                <div className="op-confirm">
                  <div className="op-confirm__icon">✅</div>
                  <h3>تأكيد إنشاء الطلب</h3>
                  <div className="op-confirm-rows">
                    <div><span>العميل</span><strong>{form.customer_name}</strong></div>
                    <div><span>نوع المياه</span><strong>{WATER_LABELS[form.water_type]||form.water_type}</strong></div>
                    <div><span>الكمية</span><strong>{form.quantity_tons} طن</strong></div>
                    {form.delivery_address && <div><span>العنوان</span><strong>{form.delivery_address}</strong></div>}
                    {form.ship_id && <div><span>الشيب</span><strong>{ships.find(s=>s.id===form.ship_id)?.name||'—'}</strong></div>}
                    {preview && <div><span>الإجمالي المتوقع</span><strong style={{color:'var(--tenant-primary)'}}>{preview.total.toFixed(2)}{' '}<SAR className="h-[0.8em] w-auto" /></strong></div>}
                    <div><span>طريقة الدفع</span><strong>{form.payment_method==='cash'?'نقدي':form.payment_method==='card'?'بطاقة':form.payment_method==='mada'?'مدى':'تحويل بنكي'}</strong></div>
                    {form.scheduled_for && <div><span>الجدولة</span><strong>{fmtDate(form.scheduled_for)}</strong></div>}
                  </div>
                  <p className="op-confirm__note">سيتم إنشاء الطلب بحالة "قيد الانتظار" ثم تعيين سائق له.</p>
                </div>
              )}

            </div>
            {/* Footer */}
            <div className="op-modal__foot">
              <button className="op-btn op-btn--ghost" onClick={()=>step===1?setShowNew(false):setStep(s=>s-1)}>
                {step===1?'إلغاء':'السابق'}
              </button>
              {step<3 ? (
                <button className="op-btn op-btn--primary" onClick={async()=>{
                  if(step===1){
                    if(!form.customer_id){showError('اختر عميلاً');return;}
                    if(!form.quantity_tons||parseFloat(form.quantity_tons)<=0){showError('أدخل كمية صحيحة');return;}
                    await fetchShips(); setStep(2);
                  } else { setStep(3); }
                }}>التالي</button>
              ) : (
                <button className="op-btn op-btn--primary" onClick={submitOrder} disabled={submitting}>{submitting?'جاري الإنشاء...':'إنشاء الطلب'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .op-page{padding:1.5rem;max-width:1400px;margin:0 auto;}
        .op-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:1.25rem;gap:1rem;flex-wrap:wrap;}
        .op-header h1{margin:0;font-size:1.5rem;}.op-header p{margin:2px 0 0;color:var(--tenant-text-secondary);font-size:.875rem;}
        .op-header__actions{display:flex;gap:.5rem;}
        .op-stats{display:flex;gap:.75rem;margin-bottom:1.25rem;flex-wrap:wrap;}
        .op-stat{background:var(--tenant-bg-secondary);border-radius:10px;padding:.75rem 1.25rem;cursor:pointer;border:2px solid transparent;transition:all .15s;min-width:110px;text-align:center;}
        .op-stat:hover{border-color:var(--tenant-primary);}.op-stat--active{border-color:var(--tenant-primary);background:color-mix(in srgb,var(--tenant-primary) 8%,transparent);}
        .op-stat--hl{background:linear-gradient(135deg,color-mix(in srgb,var(--tenant-primary) 12%,transparent),color-mix(in srgb,var(--tenant-primary) 6%,transparent));}
        .op-stat__num{display:block;font-size:1.6rem;font-weight:700;color:var(--tenant-primary);}
        .op-stat__lbl{font-size:.75rem;color:var(--tenant-text-secondary);}
        .op-accepted-banner{background:linear-gradient(135deg,#f59e0b15,#f59e0b08);border:1.5px solid #f59e0b55;border-radius:12px;padding:1rem 1.25rem;margin-bottom:1.25rem;display:flex;align-items:center;gap:.75rem;flex-wrap:wrap;}
        .op-accepted-banner svg{width:20px;height:20px;color:#f59e0b;flex-shrink:0;}
        .op-accepted-banner strong{color:#92400e;font-size:.9rem;}
        .op-accepted-pills{display:flex;gap:.5rem;flex-wrap:wrap;margin-right:auto;}
        .op-accepted-pill{padding:4px 12px;border-radius:20px;background:#f59e0b;color:#fff;border:none;cursor:pointer;font-size:.8rem;font-weight:600;}
        .op-accepted-pill:hover{background:#d97706;}
        .op-filters{display:flex;gap:.75rem;margin-bottom:1.25rem;flex-wrap:wrap;}
        .op-search{display:flex;align-items:center;gap:.5rem;background:var(--tenant-bg-secondary);border-radius:8px;padding:.5rem .75rem;flex:1;min-width:200px;}
        .op-search svg{width:16px;height:16px;color:var(--tenant-text-secondary);flex-shrink:0;}
        .op-search input{border:none;background:transparent;outline:none;width:100%;font-size:.9rem;color:var(--tenant-text);}
        .op-select{padding:.5rem .75rem;border:1px solid var(--tenant-border);border-radius:8px;background:var(--tenant-bg);color:var(--tenant-text);font-size:.875rem;}
        .op-table-wrap{overflow-x:auto;border-radius:12px;background:var(--tenant-bg-secondary);}
        .op-table{width:100%;border-collapse:collapse;font-size:.875rem;}
        .op-table th{padding:.75rem 1rem;text-align:right;font-weight:600;color:var(--tenant-text-secondary);border-bottom:2px solid var(--tenant-border);white-space:nowrap;}
        .op-table td{padding:.75rem 1rem;border-bottom:1px solid var(--tenant-border);}
        .op-tr:hover{background:var(--tenant-bg);}
        .op-tr--accepted{background:linear-gradient(90deg,color-mix(in srgb,#f59e0b 6%,transparent),transparent)!important;}
        .op-tr--accepted:hover{background:linear-gradient(90deg,color-mix(in srgb,#f59e0b 10%,transparent),transparent)!important;}
        .op-order-num{font-weight:700;color:var(--tenant-primary);font-family:monospace;}
        .op-cell-user{display:flex;flex-direction:column;}.op-cell-user small{color:var(--tenant-text-secondary);font-size:11px;}
        .op-water-tag{font-size:.8rem;padding:3px 8px;background:var(--tenant-bg);border-radius:20px;}
        .op-amount{color:var(--tenant-primary);}
        .op-no-driver{display:flex;align-items:center;gap:4px;color:#f59e0b;font-size:.8rem;}
        .op-no-driver svg{width:14px;height:14px;}
        .op-date{font-size:11px;color:var(--tenant-text-secondary);white-space:nowrap;}
        .op-eye-btn{background:none;border:none;padding:6px;cursor:pointer;color:var(--tenant-text-secondary);border-radius:6px;}
        .op-eye-btn:hover{background:var(--tenant-bg);color:var(--tenant-primary);}
        .op-eye-btn svg{width:16px;height:16px;}
        .op-badge{padding:3px 10px;border-radius:20px;font-size:.75rem;font-weight:600;white-space:nowrap;}
        .op-badge--warning{background:#fef3c7;color:#92400e;}
        .op-badge--info{background:#dbeafe;color:#1e40af;}
        .op-badge--primary{background:color-mix(in srgb,var(--tenant-primary) 15%,transparent);color:var(--tenant-primary);}
        .op-badge--blue{background:#ede9fe;color:#5b21b6;}
        .op-badge--success{background:#dcfce7;color:#166534;}
        .op-badge--danger{background:#fee2e2;color:#991b1b;}
        .op-loading,.op-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:50vh;gap:1rem;color:var(--tenant-text-secondary);}
        .op-empty svg{width:48px;height:48px;}
        .op-loading-sm{display:flex;align-items:center;gap:.5rem;padding:1rem;color:var(--tenant-text-secondary);font-size:.875rem;}
        .op-spinner{width:40px;height:40px;border:3px solid var(--tenant-border);border-top-color:var(--tenant-primary);border-radius:50%;animation:spin .8s linear infinite;}
        .op-spinner--sm{width:18px;height:18px;border-width:2px;}
        @keyframes spin{to{transform:rotate(360deg)}}
        .op-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;}
        .op-modal{background:var(--tenant-bg);border-radius:16px;width:100%;max-width:520px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;}
        .op-modal--lg{max-width:720px;}.op-modal--new{max-width:600px;}
        .op-modal__head{display:flex;justify-content:space-between;align-items:flex-start;padding:1.25rem 1.5rem;border-bottom:1px solid var(--tenant-border);}
        .op-modal__head h2{margin:0 0 6px;font-size:1.2rem;}
        .op-modal__head>button{background:none;border:none;cursor:pointer;padding:4px;color:var(--tenant-text-secondary);}
        .op-modal__head>button svg{width:20px;height:20px;}
        .op-modal__body{padding:1.5rem;overflow-y:auto;flex:1;}
        .op-modal__foot{display:flex;justify-content:flex-end;gap:.5rem;padding:1rem 1.5rem;border-top:1px solid var(--tenant-border);}
        .op-steps{display:flex;gap:.75rem;flex-wrap:wrap;}
        .op-step{font-size:.8rem;color:var(--tenant-text-secondary);padding:3px 10px;border-radius:20px;background:var(--tenant-bg-secondary);}
        .op-step--active{background:var(--tenant-primary);color:#fff;}
        .op-step--done{background:#dcfce7;color:#166534;}
        .op-detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.25rem;}
        .op-detail-card{background:var(--tenant-bg-secondary);border-radius:10px;padding:1rem;}
        .op-detail-card__title{font-size:.75rem;font-weight:700;color:var(--tenant-text-secondary);text-transform:uppercase;margin-bottom:.75rem;display:flex;align-items:center;gap:6px;}
        .op-detail-card__title svg{width:14px;height:14px;}
        .op-detail-rows{display:flex;flex-direction:column;gap:8px;}
        .op-detail-rows>div{display:flex;justify-content:space-between;align-items:center;font-size:.875rem;}
        .op-detail-rows span{color:var(--tenant-text-secondary);}
        .op-section-title{font-size:.8rem;font-weight:700;color:var(--tenant-text-secondary);text-transform:uppercase;margin-bottom:.75rem;display:flex;align-items:center;gap:6px;}
        .op-section-title svg{width:14px;height:14px;}
        .op-assign-section{margin-bottom:1.25rem;padding:1rem;background:var(--tenant-bg-secondary);border-radius:10px;}
        .op-no-drivers{display:flex;align-items:center;gap:6px;color:var(--tenant-text-secondary);font-size:.875rem;padding:.5rem 0;}
        .op-no-drivers svg{width:16px;height:16px;color:#f59e0b;}
        .op-drivers-list{display:flex;flex-direction:column;gap:.5rem;max-height:280px;overflow-y:auto;}
        .op-driver-card{display:flex;align-items:center;gap:.75rem;padding:.75rem;background:var(--tenant-bg);border-radius:8px;border:1px solid var(--tenant-border);}
        .op-driver-card__info{flex:1;}
        .op-driver-card__name{font-weight:600;font-size:.9rem;}
        .op-driver-card__meta{display:flex;gap:.75rem;align-items:center;flex-wrap:wrap;margin-top:4px;font-size:.8rem;color:var(--tenant-text-secondary);}
        .op-dist{display:flex;align-items:center;gap:3px;}
        .op-dist svg{width:12px;height:12px;}
        .op-status-section{margin-bottom:1rem;}
        .op-status-btns{display:flex;gap:.5rem;flex-wrap:wrap;}
        .op-notes{background:var(--tenant-bg-secondary);border-radius:8px;padding:.75rem 1rem;font-size:.875rem;color:var(--tenant-text-secondary);}
        .op-notes span{font-weight:600;color:var(--tenant-text);margin-left:.5rem;}
        /* New order form */
        .op-form{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}
        .op-fgroup{display:flex;flex-direction:column;gap:.35rem;}.op-fgroup--full{grid-column:span 2;}
        .op-fgroup label{font-size:.8rem;font-weight:600;color:var(--tenant-text-secondary);text-transform:uppercase;letter-spacing:.3px;}
        .op-input{padding:.6rem .75rem;border:1px solid var(--tenant-border);border-radius:8px;background:var(--tenant-bg);color:var(--tenant-text);font-size:.9rem;width:100%;box-sizing:border-box;}
        .op-input:focus{outline:none;border-color:var(--tenant-primary);}
        .op-water-btns{display:flex;gap:.5rem;flex-wrap:wrap;}
        .op-water-btn{padding:.5rem 1rem;border:2px solid var(--tenant-border);border-radius:8px;background:var(--tenant-bg);cursor:pointer;font-size:.875rem;transition:all .15s;}
        .op-water-btn.active{border-color:var(--tenant-primary);background:color-mix(in srgb,var(--tenant-primary) 10%,transparent);color:var(--tenant-primary);}
        .op-customer-search{position:relative;}
        .op-customer-search input{width:100%;box-sizing:border-box;padding:.6rem .75rem;border:1px solid var(--tenant-border);border-radius:8px;background:var(--tenant-bg);color:var(--tenant-text);font-size:.9rem;}
        .op-customer-search input:focus{outline:none;border-color:var(--tenant-primary);}
        .op-cust-drop{position:absolute;top:calc(100% + 4px);right:0;left:0;background:var(--tenant-bg);border:1px solid var(--tenant-border);border-radius:8px;box-shadow:0 4px 16px rgba(0,0,0,.12);z-index:10;max-height:200px;overflow-y:auto;}
        .op-cust-item{display:flex;justify-content:space-between;align-items:center;padding:.6rem 1rem;cursor:pointer;font-size:.875rem;}
        .op-cust-item:hover{background:var(--tenant-bg-secondary);}
        .op-cust-item small{color:var(--tenant-text-secondary);font-size:.8rem;}
        .op-selected-cust{background:#dcfce7;color:#166534;padding:6px 10px;border-radius:6px;font-size:.8rem;display:flex;align-items:center;gap:6px;margin-top:6px;}
        .op-selected-cust svg{width:14px;height:14px;}
        .op-ships-list{display:flex;flex-direction:column;gap:.5rem;max-height:300px;overflow-y:auto;}
        .op-ship-card{display:flex;align-items:center;gap:.75rem;padding:.75rem 1rem;background:var(--tenant-bg);border:2px solid var(--tenant-border);border-radius:8px;cursor:pointer;transition:all .15s;position:relative;}
        .op-ship-card:hover,.op-ship-card--sel{border-color:var(--tenant-primary);background:color-mix(in srgb,var(--tenant-primary) 5%,transparent);}
        .op-ship-card__info{flex:1;}.op-ship-card__info strong{display:block;}.op-ship-card__info small{color:var(--tenant-text-secondary);font-size:.8rem;}
        .op-ship-card__meta{display:flex;flex-direction:column;gap:4px;align-items:flex-end;font-size:.8rem;color:var(--tenant-text-secondary);}
        .op-ship-check{position:absolute;top:8px;left:8px;color:var(--tenant-primary);display:flex;}
        .op-ship-check svg{width:16px;height:16px;}
        .op-pricings{display:flex;flex-direction:column;gap:.5rem;}
        .op-pricing-card{padding:.75rem 1rem;border:2px solid var(--tenant-border);border-radius:8px;cursor:pointer;transition:all .15s;}
        .op-pricing-card:hover,.op-pricing-card--sel{border-color:var(--tenant-primary);background:color-mix(in srgb,var(--tenant-primary) 5%,transparent);}
        .op-pricing-card strong{display:block;font-size:.9rem;}.op-pricing-card span{font-size:.8rem;color:var(--tenant-text-secondary);}
        .op-price-preview{background:linear-gradient(135deg,color-mix(in srgb,var(--tenant-primary) 8%,transparent),color-mix(in srgb,var(--tenant-primary) 4%,transparent));border:1px solid color-mix(in srgb,var(--tenant-primary) 20%,transparent);border-radius:10px;padding:1rem;}
        .op-price-preview__title{font-size:.8rem;font-weight:700;color:var(--tenant-primary);text-transform:uppercase;margin-bottom:.75rem;}
        .op-price-rows{display:flex;flex-direction:column;gap:6px;}
        .op-price-rows>div{display:flex;justify-content:space-between;font-size:.875rem;color:var(--tenant-text-secondary);}
        .op-price-rows__total{border-top:1px solid var(--tenant-border);padding-top:6px;margin-top:4px;}
        .op-price-rows__total span,.op-price-rows__total strong{color:var(--tenant-text);font-size:1.1rem;}
        .op-price-rows__total strong{color:var(--tenant-primary);font-size:1.2rem;}
        .op-confirm{text-align:center;padding:1rem 0;}
        .op-confirm__icon{font-size:3rem;margin-bottom:.75rem;}
        .op-confirm h3{margin:0 0 1.5rem;}
        .op-confirm-rows{background:var(--tenant-bg-secondary);border-radius:10px;padding:1rem;text-align:right;display:flex;flex-direction:column;gap:10px;margin-bottom:1rem;}
        .op-confirm-rows>div{display:flex;justify-content:space-between;font-size:.875rem;}
        .op-confirm-rows span{color:var(--tenant-text-secondary);}
        .op-confirm__note{color:var(--tenant-text-secondary);font-size:.8rem;}
        .op-btn{display:inline-flex;align-items:center;gap:.4rem;padding:.55rem 1.1rem;border:none;border-radius:8px;cursor:pointer;font-size:.875rem;font-weight:500;transition:all .15s;}
        .op-btn svg{width:16px;height:16px;}
        .op-btn--primary{background:var(--tenant-primary);color:#fff;}
        .op-btn--primary:hover{opacity:.9;}
        .op-btn--ghost{background:var(--tenant-bg-secondary);color:var(--tenant-text);}
        .op-btn--ghost:hover{background:var(--tenant-bg);}
        .op-btn--success{background:#dcfce7;color:#166534;}
        .op-btn--blue{background:#ede9fe;color:#5b21b6;}
        .op-btn--danger-ghost{background:#fee2e2;color:#991b1b;}
        .op-btn--sm{padding:.35rem .75rem;font-size:.8rem;}
        .op-btn:disabled{opacity:.6;cursor:not-allowed;}
        .op-spin-sm{width:14px;height:14px;border:2px solid var(--tenant-border);border-top-color:var(--tenant-primary);border-radius:50%;animation:spin .8s linear infinite;display:inline-block;}
        @media(max-width:700px){.op-detail-grid{grid-template-columns:1fr;}.op-form{grid-template-columns:1fr;}.op-fgroup--full{grid-column:span 1;}}
      `}</style>
    </div>
  );
};

export default OrdersPage;
